import { useParams } from "#routers/hooks";
import { FileDataDisplay } from "#src/components/DataDisplay/FileDataDisplay";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { RecordContext, RecordProvider } from "#src/contexts/RecordContext";
import {
  getIconVariantFromFileExtension,
  Icon,
  KeyValueTile,
  MetricTile,
  NumberDataDisplay,
  Panel,
} from "@validereinc/common-components";
import { getFileExtensionFromFilename } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { truncate } from "es-toolkit/compat";
import React, { useContext } from "react";
import styles from "./RecordValueSummaryPanel.module.css";

const cx = classNames.bind(styles);

export const RecordValueSummaryPanelContent = () => {
  const { record, isLoading: isRecordLoading } =
    useContext(RecordContext) ?? {};
  const { measurementType } = useParams<{ measurementType: string }>();
  const {
    getTypeName,
    getUnitName,
    getPrecisionByType,
    isLoading: isMeasurementTypesLoading,
  } = useMeasurementTypes();

  const precision = getPrecisionByType(measurementType);

  const isLoading = isRecordLoading || isMeasurementTypesLoading;

  const recordValue = record?.values.find(
    ({ measurement_type }) => measurement_type === measurementType
  );

  return (
    <>
      <Panel
        title={getTypeName(measurementType)}
        isFluidY={false}
      >
        <div className={cx("items")}>
          <MetricTile
            isLoading={isLoading}
            title={getTypeName(measurementType)}
            value={(props) => (
              <NumberDataDisplay
                {...props}
                value={recordValue?.value}
                formattingOpts={precision ? { fractionDigits: precision } : {}}
              />
            )}
            unit={getUnitName(
              recordValue?.measurement_unit ?? "",
              recordValue?.value ?? undefined
            )}
          />
          {recordValue?.note && (
            <KeyValueTile
              title="Note"
              value={truncate(recordValue.note, { length: 500 })}
              isLoading={isLoading}
            />
          )}
          {recordValue?.attachment_filename && (
            <KeyValueTile
              title="Attachment"
              isLoading={isLoading}
              value={(props) => (
                <div
                  className={cx("attachment-name-and-icon", props.className)}
                >
                  <Icon
                    variant={getIconVariantFromFileExtension(
                      getFileExtensionFromFilename(
                        recordValue.attachment_filename ?? ""
                      )
                    )}
                    size={40}
                  />
                  <FileDataDisplay
                    fileName={recordValue.attachment_filename ?? "Unknown"}
                    fileId={recordValue?.attachment_ref ?? ""}
                    hideIcon
                  />
                </div>
              )}
            />
          )}
        </div>
      </Panel>
    </>
  );
};

export const RecordValueSummaryPanel = () => {
  return (
    <RecordProvider>
      <RecordValueSummaryPanelContent />
    </RecordProvider>
  );
};
